import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Download, Plus, Rss, Settings2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CopyButton } from '@/components/CopyButton';
import { FeedPreview } from '@/components/FeedPreview';
import { FollowedUserCard } from '@/components/FollowedUserCard';
import { useFollowedNpubs } from '@/hooks/useFollowedNpubs';
import { useProfilesMap } from '@/hooks/useProfilesMap';
import { useToast } from '@/hooks/useToast';
import { buildFeedUrl, buildOpml, downloadFile, MAX_FOLLOWS, parseNpubInput } from '@/lib/feed';

const Index = () => {
  useSeoMeta({
    title: 'NostrRSS — Follow npubs in your RSS reader',
    description: 'Turn Nostr notes and long-form articles from specific npubs into RSS feeds you can subscribe to in any reader.',
  });

  const { toast } = useToast();
  const { users, addUser, removeUser, options, setOptions } = useFollowedNpubs();
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const origin = window.location.origin;
  const pubkeys = useMemo(() => users.map((user) => user.pubkey), [users]);
  const npubs = useMemo(() => users.map((user) => user.npub), [users]);

  const profiles = useProfilesMap(pubkeys);

  const combinedFeedUrl = useMemo(
    () => buildFeedUrl(origin, npubs, options),
    [origin, npubs, options],
  );

  const handleAdd = () => {
    const parsed = parseNpubInput(input);
    if (!parsed) {
      setError('That doesn’t look like a Nostr key. Paste an npub1…, nprofile1…, or hex pubkey.');
      return;
    }

    const result = addUser(parsed.npub, parsed.pubkey);
    if (result === 'duplicate') {
      setError('You’re already following this person.');
    } else if (result === 'full') {
      setError(`You can follow up to ${MAX_FOLLOWS} people.`);
    } else {
      setInput('');
      setError(null);
      toast({ title: 'Followed', description: 'Their feed URL is ready below.' });
    }
  };

  const handleOpmlExport = () => {
    const named = users.map((user) => {
      const metadata = profiles.data?.get(user.pubkey);
      const name = metadata?.display_name || metadata?.name || user.npub.slice(0, 12);
      return { name, npub: user.npub };
    });
    downloadFile('nostrss.opml', buildOpml(named, origin, options), 'text/x-opml');
    toast({ title: 'OPML downloaded', description: 'Import it into your RSS reader to subscribe to everyone at once.' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-background to-background dark:from-orange-950/20 dark:via-background dark:to-background">
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="size-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md shadow-orange-500/20">
            <Rss className="size-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-xl tracking-tight leading-tight">NostrRSS</h1>
            <p className="text-xs text-muted-foreground">Follow npubs in any RSS reader</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="size-4" />
              Follow someone
            </CardTitle>
            <CardDescription>
              Paste an npub (or nprofile / hex pubkey) to generate their RSS feed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                }}
                placeholder="npub1…"
                className="font-mono text-sm"
                aria-label="npub to follow"
                aria-invalid={error ? true : undefined}
              />
              <Button onClick={handleAdd} className="shrink-0 bg-orange-600 hover:bg-orange-700 text-white">
                <Plus className="size-4" />
                Follow
              </Button>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <p className="text-xs text-muted-foreground">
              {users.length} / {MAX_FOLLOWS} followed
            </p>
          </CardContent>
        </Card>

        {users.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center space-y-3">
              <div className="mx-auto size-12 rounded-full bg-orange-100 dark:bg-orange-950 flex items-center justify-center">
                <Users className="size-6 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-muted-foreground max-w-sm mx-auto">
                Follow your first npub above and you’ll get a personal RSS feed URL for them —
                plus an OPML file to import everyone into your reader at once.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings2 className="size-4" />
                  Feed content
                </CardTitle>
                <CardDescription>Applies to every feed URL below.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <OptionRow
                  id="opt-notes"
                  label="Short notes"
                  description="Kind 1 posts (tweets, thoughts, links)."
                  checked={options.notes}
                  onCheckedChange={(checked) => {
                    if (!checked && !options.articles) return;
                    setOptions((prev) => ({ ...prev, notes: checked }));
                  }}
                />
                <OptionRow
                  id="opt-articles"
                  label="Long-form articles"
                  description="Kind 30023 posts (blog-style, rendered from markdown)."
                  checked={options.articles}
                  onCheckedChange={(checked) => {
                    if (!checked && !options.notes) return;
                    setOptions((prev) => ({ ...prev, articles: checked }));
                  }}
                />
                <OptionRow
                  id="opt-replies"
                  label="Include replies"
                  description="Off by default so your feed isn’t full of half-conversations."
                  checked={options.replies}
                  onCheckedChange={(checked) => setOptions((prev) => ({ ...prev, replies: checked }))}
                />
              </CardContent>
            </Card>

            <Card className="border-orange-200 dark:border-orange-900">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Rss className="size-4 text-orange-600" />
                  Combined feed
                </CardTitle>
                <CardDescription>
                  Everyone you follow in a single feed. Prefer separate feeds? Each person has
                  their own URL below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 min-w-0 truncate rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground"
                    title={combinedFeedUrl}
                  >
                    {combinedFeedUrl}
                  </code>
                  <CopyButton text={combinedFeedUrl} label="Copy combined feed URL" />
                </div>
                <Button variant="outline" size="sm" onClick={handleOpmlExport}>
                  <Download className="size-4" />
                  Download OPML (all feeds)
                </Button>
              </CardContent>
            </Card>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                People you follow
              </h2>
              <div className="space-y-3">
                {users.map((user) => (
                  <FollowedUserCard
                    key={user.pubkey}
                    user={user}
                    metadata={profiles.data?.get(user.pubkey)}
                    profilesLoading={profiles.isLoading}
                    feedUrl={buildFeedUrl(origin, [user.npub], options)}
                    onRemove={removeUser}
                  />
                ))}
              </div>
            </section>

            <FeedPreview pubkeys={pubkeys} profiles={profiles.data ?? new Map()} options={options} />
          </>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-4 py-8 text-center space-y-2">
        <p className="text-xs text-muted-foreground">
          Feed URLs activate as soon as this site is deployed (project menu → Deploy → Cloudflare).
          Feeds update every ~10 minutes and are served by the site’s own worker.
        </p>
        <p className="text-xs text-muted-foreground">
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </p>
      </footer>
    </div>
  );
};

interface OptionRowProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function OptionRow({ id, label, description, checked, onCheckedChange }: OptionRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium cursor-pointer">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

export default Index;
